import { memo } from 'react';
import { Check, Loader2, X } from 'lucide-react';
import clsx from 'clsx';
import type { DisplayMessage } from '../../hooks/useJames';
import { JamesTypingIndicator } from './JamesTypingIndicator';
import { formatTime, formatDayMonthTime } from '../../utils/dateFormat';
import { SimpleMarkdown } from '../ui/SimpleMarkdown';

interface JamesMessageProps {
  message: DisplayMessage;
}

export const JamesMessage = memo(function JamesMessage({ message }: JamesMessageProps) {
  const isUser = message.role === 'user';
  const isStreaming = message.isStreaming && !message.content;
  const isStillWorking = message.isStreaming && !!message.content;
  const senderLabel = isUser ? 'You' : 'James';

  return (
    <div
      className={clsx('flex gap-3', {
        'flex-row-reverse': isUser,
      })}
    >
      <div
        className={clsx(
          'flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-2xl text-body-xs font-semibold',
          isUser
            ? 'bg-surface-inverse text-text-inverse'
            : 'border border-border-light bg-surface-primary text-text-primary'
        )}
      >
        {isUser ? 'Y' : 'J'}
      </div>

      <div
        className={clsx('flex max-w-[min(90%,48rem)] flex-col gap-2', {
          'items-end': isUser,
        })}
      >
        <div className={clsx('flex items-center gap-2 px-1', { 'flex-row-reverse': isUser })}>
          <span className="text-body-xs font-semibold uppercase tracking-[0.18em] text-text-tertiary">
            {senderLabel}
          </span>
          <span className="text-body-xs text-text-muted">{formatTimestamp(message.createdAt)}</span>
        </div>

        <div
          className={clsx('rounded-2xl border px-4 py-3.5', {
            'border-surface-inverse bg-surface-inverse text-text-inverse': isUser,
            'border-border-light bg-surface-primary text-text-primary': !isUser,
          })}
        >
          {isStreaming ? (
            <JamesTypingIndicator />
          ) : (
            <div className="whitespace-pre-wrap break-words text-body-sm leading-7">
              <SimpleMarkdown content={message.content} />
            </div>
          )}
        </div>

        {message.streamingTools && message.streamingTools.size > 0 && (
          <div className="mt-1 flex flex-wrap gap-2">
            {Array.from(message.streamingTools.entries()).map(([id, tool]) => (
              <ToolCallBadge
                key={id}
                name={tool.name}
                status={tool.status}
                errorMessage={
                  tool.status === 'error' && tool.result && typeof tool.result === 'object' && 'error' in tool.result
                    ? String((tool.result as { error: unknown }).error)
                    : undefined
                }
              />
            ))}
          </div>
        )}

        {isStillWorking && (
          <div className="mt-1 flex items-center gap-2 px-1">
            <div className="flex gap-0.5">
              <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-text-tertiary" style={{ animationDelay: '0ms' }} />
              <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-text-tertiary" style={{ animationDelay: '150ms' }} />
              <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-text-tertiary" style={{ animationDelay: '300ms' }} />
            </div>
            <span className="text-body-xs text-text-tertiary">James is still working...</span>
          </div>
        )}
      </div>
    </div>
  );
});

interface ToolCallBadgeProps {
  name: string;
  status: 'running' | 'completed' | 'error';
  errorMessage?: string;
}

function ToolCallBadge({ name, status, errorMessage }: ToolCallBadgeProps) {
  const displayName = name.replace('attio_', '').replace(/_/g, ' ');

  return (
    <div className="inline-flex flex-col">
      <span
        className={clsx(
          'inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-body-xs',
          {
            'border-border-light bg-surface-primary text-text-secondary': status === 'running',
            'border-success/20 bg-success-light text-success-dark': status === 'completed',
            'border-error/20 bg-error-light text-error-dark': status === 'error',
          }
        )}
      >
        {status === 'running' && (
          <Loader2 className="h-3 w-3 animate-spin" />
        )}
        {status === 'completed' && (
          <Check className="h-3 w-3" />
        )}
        {status === 'error' && (
          <X className="h-3 w-3" />
        )}
        {displayName}
      </span>
      {errorMessage && (
        <span
          className="mt-1 max-w-[220px] px-2 text-[11px] leading-tight text-error"
          title={errorMessage}
        >
          {errorMessage}
        </span>
      )}
    </div>
  );
}

function formatTimestamp(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();
  return isToday ? formatTime(date) : formatDayMonthTime(date);
}
